/**
 * PATH: packages/email-hub/src/feature/hooks/useEmails.merge.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-EMAILS-MERGE-001
 * PURPOSE: Pure row merges that preserve enrichment across re-fetches —
 *          list refresh/poll/Phase-1 (mergeRefreshedEmails) and the single
 *          detail GET over its cached seed (mergeDetailOverSeed).
 * WHY: Extracted from the useEmails god-hook (ARC EMAIL_HUB_LOADING_
 *      RESILIENCE phase 3, expand→migrate→contract). Pure I→O
 *      (prev[], results[]) → Email[] — no React, no module state — so
 *      the move is zero behaviour change. Re-exported from ./useEmails.
 * RELATED:
 *   - ./useEmails.ts: imports + re-exports this for byte-identical API.
 *   - useEmails.progressive.test.ts / mergeRefreshedEmails pins.
 */
import type { Email } from './useEmails.types'

/** BUG-2026-05-24-EMAIL-CHIP-FLICKER-ON-POLL-REFETCH — merge incoming
 *  refresh / poll / Phase-1 results onto the previously-painted rows
 *  WITHOUT dropping the enrichment fields that only the Phase-2 / full
 *  fetch returns.
 *
 *  Why this exists:
 *    Phase 1 (`?titles_only=true`, ~200 ms) returns a subset of the
 *    Phase 2 row shape to keep first paint fast — `mailboxes`,
 *    `synced_by_email`, `synced_by_name`, `on_card_*`, `job_name`,
 *    `quote_number`, CRM/`company_*` are omitted from the wire.
 *    NOTE (EMAIL_AI_INSTANT_TAG §P2, 2026-05-31): the AI review chips
 *    `allocation_summary` (the "Triage"/board chip + its ✓/✕),
 *    `suggested_job` and `job_auto_status`/`job_auto_score` are now
 *    INCLUDED in Phase 1 (the BE builds the cheap batched maps + reads
 *    the persisted `suggested_job` column on the lean path), so those
 *    chips paint WITH the row instead of popping in at Phase 2. The
 *    merge below still preserves the remaining Phase-2-only fields.
 *    The 10-s visibility poll AND the 6/15-s sync-queued
 *    refetches AND the focus-refetch all share the same code path,
 *    and all of them re-fire Phase 1 → Phase 2. If we naively replace
 *    `prev` with the Phase 1 results, every row's chip payload blanks
 *    for ~200-400 ms (the gap before Phase 2 lands) → operator sees
 *    `Mark` flip to `?`, kanban-card badge (`#211 ✓`) disappear,
 *    category chip flash empty. Every 10 s, forever.
 *
 *  Contract:
 *    Output row positions follow `results` (server-side ordering wins
 *    on a refetch). For each `results[i]`, if a row with the same `id`
 *    exists in `prev`, the output is `{ ...prevRow, ...results[i] }`
 *    — object spread is field-by-field safe, so any key that the new
 *    row OMITS keeps its prev value, while any key it sends explicitly
 *    (even a `false`/empty-string/null) wins. Brand-new rows pass
 *    through unchanged.
 *
 *  Edge cases:
 *    - Empty `prev` → just returns `results` (no-op merge).
 *    - Filter / search / category change → new query returns a
 *      different ID set; rows that no longer match are correctly
 *      absent from the output, rows that DO match keep prev's
 *      enrichment (no unnecessary blank-chip flash when toggling
 *      filters too).
 *    - Row in `prev` with no `id` (defensive — shouldn't happen) →
 *      skipped from the Map lookup; the matching `results` row passes
 *      through as-is.
 *    - Row in `results` with no `id` → skipped entirely (we can't
 *      render a row without an id; safer than letting React fight a
 *      missing-key warning).
 *
 *  Exported so a regression test pins the contract directly without
 *  needing to scaffold the hook.
 */
export function mergeRefreshedEmails(prev: Email[], results: Email[]): Email[] {
  if (prev.length === 0) return results.filter(r => r && typeof r.id === 'string')
  const prevById = new Map<string, Email>()
  for (const r of prev) {
    if (r && typeof r.id === 'string') prevById.set(r.id, r)
  }
  const out: Email[] = []
  const seen = new Set<string>()
  let oldestRefreshed: string | null = null
  for (const row of results) {
    if (!row || typeof (row as Email).id !== 'string') continue
    const prevRow = prevById.get(row.id)
    out.push(prevRow ? { ...prevRow, ...row } : row)
    seen.add(row.id)
    const ts = (row as Email).received_at
    if (ts && (oldestRefreshed === null || ts < oldestRefreshed)) {
      oldestRefreshed = ts
    }
  }
  // BUG-2026-06-11-POLL-TRUNCATES-PAGINATION: a poll/refresh tick only
  // re-fetches PAGE 1. When the operator had clicked "Load more" (400+
  // rows on screen), naively returning `results` snapped the list back
  // to 200 rows and threw away their scroll position every 30s.
  // Preserve the previously-loaded TAIL — prev rows strictly OLDER than
  // the refreshed window. Rows INSIDE the window that are absent from
  // `results` are still correctly dropped (archived / deleted / moved
  // server-side); ISO-8601 strings compare lexicographically.
  if (oldestRefreshed !== null) {
    for (const r of prev) {
      if (!r || typeof r.id !== 'string' || seen.has(r.id)) continue
      if (r.received_at && r.received_at < oldestRefreshed) out.push(r)
    }
  }
  return out
}

/** Merge one DETAIL response over the list-cache seed it replaced.
 *
 *  Why this exists (change #302): `useEmail` seeded synchronously from the
 *  list cache and then REPLACED that row with the detail response, on the
 *  documented assumption that "the detail row carries strictly MORE
 *  information than the list row". That assumption was false. The detail
 *  serializer omitted the receiving-mailbox provenance (`synced_by_mailbox_
 *  email` and friends) that the list serializer sends, so opening an email
 *  deleted it — the reader's "via abhishek@finsoeasy.com" line painted for
 *  one frame from the seed and then vanished.
 *
 *  This is the SECOND time the same invariant broke. BUG-2026-06-15-EMAIL-
 *  READER-CC-BCC-FLICKER fixed it inside `rememberEmailSummary`, which now
 *  shallow-merges, so the shared cache kept the union all along. React state
 *  was the one remaining clobber, one layer up from the guarded one. Fixing
 *  the layer that reported the symptom is what left the sibling exposed —
 *  see RECURRENCE-2026-07-25-DETAIL-REPLACES-SEED.
 *
 *  The backend now sends those fields too, but a serializer field list is
 *  not a load-bearing place to keep an invariant: the next omission would
 *  break the reader the same silent way. This is the structural half.
 *
 *  Contract — identical to `mergeRefreshedEmails`, and for the identical
 *  reason: a NARROWER response may never blank a field a WIDER one already
 *  delivered.
 *    - Detail wins for every key it actually sends, INCLUDING an explicit
 *      `null`/`false`/`''`. A cleared `job_id` or a revoked `supplier_id`
 *      must not be resurrected from a stale seed.
 *    - The seed only fills keys the detail payload OMITS. `JSON.parse`
 *      drops absent keys entirely, so object spread already expresses
 *      exactly this; explicit `undefined` values (hand-built fixtures,
 *      mapped payloads) are stripped so they cannot punch holes either.
 *    - No seed → the detail row passes through unchanged.
 *
 *  Exported so the contract is pinned without scaffolding the hook.
 */
export function mergeDetailOverSeed(
  seed: Email | null | undefined,
  detail: Email,
): Email {
  if (!seed) return detail
  const merged: Record<string, unknown> = { ...seed }
  for (const [key, value] of Object.entries(detail as unknown as Record<string, unknown>)) {
    if (value === undefined) continue
    merged[key] = value
  }
  return merged as unknown as Email
}
